import { CdkDrag } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { DataTaskRequest, GridOptions, GridRow, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { GridModalData } from '@app/core/components/grid/modals/grid-modal.service';
import { ModalService } from '@app/core/services/modal.service';
import { GridBookingGet, GridBookingProcessParams, GridBookingProcessResponse, GridBookingService, GridBookingVoucher } from '@app/services/grid-booking.service';
import { debounceTime, Subject, takeUntil } from 'rxjs';

@GridModalComponent('gridBookingModal')
@Component({
    selector: 'tt-grid-booking',
    templateUrl: './grid-booking.component.html',
    styleUrls: ['./grid-booking.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class GridBookingComponent implements OnInit, OnDestroy {
    private readonly initialSize = { width: '98vw', height: '95vh' };

    @ViewChild(CdkDrag)
    private dialogDrag?: CdkDrag;

    public page?: GridBookingGet;

    public voucher = this.createEmptyVoucher();

    public rows: GridRow[] = [];

    public files: File[] = [];

    public grid?: GridOptions;

    public ready = false;

    public creatingVoucher = false;

    public posting = false;

    private dataItem: GridRow | null = null;

    private dataItems: GridRow[] = [];

    private loadData: GridBookingLoadData | null = null;

    private pendingRowChange: Promise<void> = Promise.resolve();

    private readonly refreshTotals$ = new Subject<void>();

    private readonly destroy$ = new Subject<void>();

    private resizeState?: GridBookingResizeState;

    private removePointerMoveListener?: () => void;

    private removePointerUpListener?: () => void;

    private removePointerCancelListener?: () => void;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: GridModalData,
        private dialogRef: MatDialogRef<GridBookingComponent, GridBookingProcessResponse | undefined>,
        private gridBooking: GridBookingService,
        private modal: ModalService,
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
    ) {
        this.dialogRef.addPanelClass('tt-grid-booking-dialog');
        this.dialogRef.updateSize(this.initialSize.width, this.initialSize.height);
        this.initializeDialogData(data);

        this.refreshTotals$.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => void this.refreshTotals());
    }

    ngOnInit(): void {
        void this.initialize();
    }

    private initializeDialogData(data: GridModalData) {
        this.dataItems = data.selectedRows ?? [];
        this.dataItem = data.row ?? null;
        this.loadData = (data.loadData as GridBookingLoadData | null | undefined) ?? null;

        if (this.dataItem) {
            this.voucher.amount_locval_balance = this.dataItem['amount_locval_balance'] ?? '0';
        }

        if (data.rememberId) {
            this.grid = this.createGrid(data.rememberId);
        }
    }

    private async initialize() {
        try {
            await this.loadPage();
            this.ensureVoucherDates();
            this.ready = true;
            this.refreshTotals$.next();
        } catch (error) {
            console.error(error);
            await this.modal.openErrorDialog('' + error);
        }
    }

    private createGrid(rememberId: string): GridOptions {
        return new Grid()
            .setRememberId(rememberId + '.gridbooking')
            .setLoadDataFunction({ method: 3339, parameters: null })
            .setToolbar({
                hidden: false,
                excelExport: false,
                pdfExport: false,
                filter: false,
                headers: false,
                layouts: false,
                read: false,
            })
            .addToolbarButton({ name: 'grid_booking_new_row', text: 'new_row', func: () => this.addRow(), icon: 'far fa-plus', cssClass: 'tt-button tt-button--success im-grid-btn-xs', translate: true })
            .addRowButton({ name: 'grid_booking_delete_row', text: '', func: (row: GridRow) => this.removeRow(row), icon: 'far fa-trash', btn_type: 'danger', cssClass: 'tt-button tt-button--danger im-grid-btn-xs', translate: true })
            .setKendoConfig({ filterable: false });
    }

    public onVoucherDateChanged(value: string) {
        this.voucher.voucherdate = value;
        this.voucher.transdate = value;
    }

    public overrideDatesToRows() {
        this.rows = this.rows.map((row) => ({
            ...row,
            voucherdate: this.voucher.voucherdate,
            transdate: this.voucher.transdate,
        }));

        this.setDataSource(this.rows);
    }

    public onGridReady() {
        this.grid?.gridfunc?.getGridApi()?.setGridOption('sideBar', {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressRowGroups: true,
                        suppressValues: true,
                    },
                },
            ],
            position: 'left',
        });

        this.setDataSource(this.rows);
    }

    private async addRow() {
        if (!this.grid?.gridfunc) return;

        const row = await this.gridBooking.addRow({
            last_row: this.rows[this.rows.length - 1],
            dataitem: this.dataItem,
        });

        this.rows.push({ ...row });
        this.setDataSource(this.rows);
    }

    private removeRow(row: GridRow) {
        const deleteIndex = this.rows.findIndex((rowToDelete) => rowToDelete['_uuid'] === row['_uuid']);

        if (deleteIndex !== -1) {
            this.rows.splice(deleteIndex, 1);
            this.setDataSource(this.rows, true);
        }
    }

    private setDataSource(rows: GridRow[], refreshTotals = false) {
        this.grid?.gridfunc?.setDataSource(rows);

        setTimeout(() => {
            this.rows = this.grid?.gridfunc?.getAllRows() ?? rows;

            if (refreshTotals) {
                this.refreshTotals$.next();
            }
        });
    }

    private async loadPage() {
        if (!this.loadData || typeof this.loadData.method !== 'number') return;

        this.page = await this.gridBooking.getPage({
            grid_datatask_keyno: this.loadData.method,
            grid_datatask_params: this.loadData.parameters,
            dataitem: this.dataItem,
            dataitems: this.dataItem !== null ? [this.dataItem] : this.dataItems,
        });

        this.voucher.voucherdate = this.page.voucherdate;
        this.voucher.transdate = this.page.transdate;
        this.voucher.note = this.page.note;
        this.rows = this.parseRows(this.page.suggested_rows);
    }

    private parseRows(value?: string): GridRow[] {
        if (!value) return [];

        try {
            return JSON.parse(value);
        } catch {
            return [];
        }
    }

    private async refreshTotals() {
        try {
            const totals = await this.gridBooking.getTotals(this.rows);

            this.voucher.amount = this.formatAmount(totals.amount);
            this.voucher.amount_vat = this.formatAmount(totals.amount_vat);
        } catch (error) {
            await this.modal.openErrorDialog('' + error);
        }
    }

    private formatAmount(value: string | number): string {
        return new Intl.NumberFormat('nb-NO', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value));
    }

    public onRowChanged(event: RowDataChangeEvent) {
        if (event.newValue === event.oldValue) return;

        this.pendingRowChange = this.pendingRowChange.then(() => this.updateRow(event.colId, event.row));
    }

    private async updateRow(changedColumn: string, row: GridRow) {
        if (!this.ready) return;

        try {
            const changes = await this.gridBooking.updateRow({ ...row, colname_changed: changedColumn });
            const rowIndex = this.rows.findIndex((currentRow) => currentRow['_uuid'] === row['_uuid']);

            if (rowIndex !== -1) {
                const updatedRow = { ...row, ...changes };

                this.rows[rowIndex] = updatedRow;
                this.grid?.gridfunc?.updateRow(updatedRow);
                this.refreshTotals$.next();
            }
        } catch (error) {
            console.error(error);
        }
    }

    public async createAcVoucher() {
        if (!this.page) return;

        try {
            this.creatingVoucher = true;
            await this.pendingRowChange;

            const response = await this.gridBooking.process(+this.page.book_datatask_keyno, {
                ...(await this.createProcessParams()),
                button: 'create_acvoucher',
            });

            await this.handleProcessResponse(response);
        } catch (error) {
            console.error(error);
        } finally {
            this.creatingVoucher = false;
        }
    }

    public async bookkeep() {
        if (!this.page) return;

        try {
            this.posting = true;
            await this.pendingRowChange;

            const response = await this.gridBooking.process(+this.page.book_datatask_keyno, {
                ...(await this.createProcessParams()),
                button: 'bookkeep',
            });

            await this.handleProcessResponse(response);
        } catch (error) {
            console.error(error);
        } finally {
            this.posting = false;
        }
    }

    private async createProcessParams(): Promise<Omit<GridBookingProcessParams, 'button'>> {
        this.ensureVoucherDates();
        this.rows = this.grid?.gridfunc?.getAllRows() ?? this.rows;

        return {
            voucher: this.voucher,
            rows: this.serializeRows(this.rows),
            files: await Promise.all(this.files.map(async (file) => ({ filename: file.name, base64: await this.toBase64(file) }))),
            grid_datatask_keyno: typeof this.loadData?.method === 'number' ? this.loadData.method : null,
            grid_datatask_params: await this.resolveParameters(this.loadData?.parameters),
        };
    }

    private async handleProcessResponse(response: GridBookingProcessResponse) {
        if (response.errorcode !== '0') {
            await this.modal.show({ type: 'warning', title: 'Advarsel', message: response.errormessage });
        } else {
            this.dialogRef.close(response);
        }
    }

    private async resolveParameters(parameters?: object | (() => unknown | Promise<unknown>) | null) {
        return typeof parameters === 'function' ? await parameters() : parameters;
    }

    private serializeRows(rows: GridRow[]) {
        return rows.map((row) => {
            const serializedRow = { ...row };

            for (const [key, value] of Object.entries(serializedRow)) {
                if (value instanceof Date) {
                    serializedRow[key] = new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString();
                }
            }

            return serializedRow;
        });
    }

    private toBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.readAsDataURL(file);
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
            reader.onerror = reject;
        });
    }

    public dismiss() {
        this.dialogRef.close();
    }

    public resetSize(): void {
        this.cancelActiveResize();
        this.dialogDrag?.reset();
        this.dialogRef.updateSize(this.initialSize.width, this.initialSize.height);
        this.dialogRef.updatePosition();
    }

    public startResize(event: PointerEvent): void {
        if (event.button !== 0 || this.resizeState) return;

        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return;

        event.preventDefault();
        event.stopPropagation();

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const handle = event.currentTarget as HTMLElement;

        this.anchorDialog(paneRect, boundaryRect);
        handle.setPointerCapture(event.pointerId);

        this.resizeState = {
            pointerId: event.pointerId,
            handle,
            startX: event.clientX,
            startY: event.clientY,
            startWidth: paneRect.width,
            startHeight: paneRect.height,
            left: paneRect.left - boundaryRect.left,
            top: paneRect.top - boundaryRect.top,
            boundaryWidth: boundaryRect.width,
            boundaryHeight: boundaryRect.height,
        };

        this.removePointerMoveListener = this.renderer.listen('document', 'pointermove', (pointerEvent: PointerEvent) => this.onPointerMove(pointerEvent));
        this.removePointerUpListener = this.renderer.listen('document', 'pointerup', (pointerEvent: PointerEvent) => this.finishResize(pointerEvent));
        this.removePointerCancelListener = this.renderer.listen('document', 'pointercancel', (pointerEvent: PointerEvent) => this.finishResize(pointerEvent));
    }

    public resizeWithKeyboard(event: KeyboardEvent): void {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;

        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return;

        event.preventDefault();
        event.stopPropagation();

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const step = event.shiftKey ? 50 : 10;
        const widthDelta = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
        const heightDelta = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;

        this.anchorDialog(paneRect, boundaryRect);
        this.updateDialogSize(
            paneRect.width + widthDelta,
            paneRect.height + heightDelta,
            paneRect.left - boundaryRect.left,
            paneRect.top - boundaryRect.top,
            boundaryRect.width,
            boundaryRect.height,
        );
    }

    private onPointerMove(event: PointerEvent): void {
        const resize = this.resizeState;

        if (!resize || resize.pointerId !== event.pointerId) return;

        event.preventDefault();
        this.updateDialogSize(
            resize.startWidth + event.clientX - resize.startX,
            resize.startHeight + event.clientY - resize.startY,
            resize.left,
            resize.top,
            resize.boundaryWidth,
            resize.boundaryHeight,
        );
    }

    private finishResize(event: PointerEvent): void {
        const resize = this.resizeState;

        if (!resize || resize.pointerId !== event.pointerId) return;

        this.cancelActiveResize();
    }

    private updateDialogSize(width: number, height: number, left: number, top: number, boundaryWidth: number, boundaryHeight: number): void {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const minWidth = Math.min(35 * rootFontSize, boundaryWidth);
        const minHeight = Math.min(20 * rootFontSize, boundaryHeight);
        const maxWidth = Math.max(minWidth, boundaryWidth - left);
        const maxHeight = Math.max(minHeight, boundaryHeight - top);
        const clampedWidth = Math.min(Math.max(width, minWidth), maxWidth);
        const clampedHeight = Math.min(Math.max(height, minHeight), maxHeight);

        this.dialogRef.updateSize(`${Math.round(clampedWidth)}px`, `${Math.round(clampedHeight)}px`);
    }

    private anchorDialog(paneRect: DOMRect, boundaryRect: DOMRect): void {
        this.dialogRef.updateSize(`${Math.round(paneRect.width)}px`, `${Math.round(paneRect.height)}px`);
        this.dialogRef.updatePosition({
            left: `${Math.round(paneRect.left - boundaryRect.left)}px`,
            top: `${Math.round(paneRect.top - boundaryRect.top)}px`,
        });
        this.dialogDrag?.reset();
    }

    private cancelActiveResize(): void {
        const resize = this.resizeState;

        if (resize?.handle.hasPointerCapture(resize.pointerId)) {
            resize.handle.releasePointerCapture(resize.pointerId);
        }

        this.resizeState = undefined;
        this.removePointerMoveListener?.();
        this.removePointerUpListener?.();
        this.removePointerCancelListener?.();
        this.removePointerMoveListener = undefined;
        this.removePointerUpListener = undefined;
        this.removePointerCancelListener = undefined;
    }

    private getDialogPane(): HTMLElement | undefined {
        return (this.elementRef.nativeElement.closest('.cdk-overlay-pane') as HTMLElement | null) ?? undefined;
    }

    private ensureVoucherDates(): void {
        if (!this.hasDate(this.voucher.voucherdate)) {
            this.voucher.voucherdate = this.hasDate(this.page?.voucherdate) ? this.page.voucherdate : this.today;
        }

        if (!this.hasDate(this.voucher.transdate)) {
            this.voucher.transdate = this.hasDate(this.page?.transdate) ? this.page.transdate : this.voucher.voucherdate;
        }
    }

    private hasDate(value: unknown): value is string {
        return typeof value === 'string' && value.trim() !== '' && value !== '0' && value !== 'Invalid date';
    }

    private createEmptyVoucher(): GridBookingVoucher {
        return {
            transdate: this.today,
            voucherdate: this.today,
            note: '',
        };
    }

    private get today() {
        const today = new Date();
        const year = today.getFullYear();
        const month = `${today.getMonth() + 1}`.padStart(2, '0');
        const day = `${today.getDate()}`.padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    ngOnDestroy(): void {
        this.cancelActiveResize();
        this.destroy$.next();
        this.destroy$.complete();
        this.refreshTotals$.complete();
    }
}

type GridBookingLoadData = Omit<DataTaskRequest, 'parameters'> & {
    parameters?: object | (() => unknown | Promise<unknown>) | null;
};

interface GridBookingResizeState {
    pointerId: number;
    handle: HTMLElement;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    left: number;
    top: number;
    boundaryWidth: number;
    boundaryHeight: number;
}
